import _styled, { default as _styled2 } from "styled-components";
import type { ComponentType } from "react";
import type { SanityRouteDocument } from "@ouster/shared";
import { useRouter } from "next/router";
import { clearRouterPath } from "~/features/app/utils";
import { useNavLayout } from "~/features/app/app-header-v2/context";
import { CaseStudiesFilters } from "~/features/case-study/case-studies-filters";
import { CaseStudiesGrid } from "~/features/case-study/case-studies-grid";
import { useBodyBackground } from "~/features/ui/use-body-background";
import { SanityPageBuilder, type SanityPageBuilderRenderSection } from "~/features/sanity/page-builder/renderer";
import { CASE_STUDIES_PAGE_SIZE } from "./constants";
import type { SanityCaseStudiesPageData } from "./types";
type PageBuilderComponent = ComponentType<{
  sections?: SanityPageBuilderRenderSection[];
}>;
type Props = {
  route: Pick<SanityRouteDocument, "title">;
  sections: SanityPageBuilderRenderSection[];
  caseStudiesData: SanityCaseStudiesPageData;
  /** Swap the section renderer when Presentation has live page-builder updates. */
  PageBuilderComponent?: PageBuilderComponent;
};

/**
 * Renders the Case Studies overview from its own Sanity query. The overview
 * document intentionally has no hero in production, so its title is retained
 * as the page's accessible heading while cards, filters, and the map render
 * from the content collection itself.
 */
export function SanityCaseStudiesPageView({
  route,
  sections,
  caseStudiesData,
  PageBuilderComponent = SanityPageBuilder
}: Props) {
  useBodyBackground({
    mode: "light"
  });
  const router = useRouter();
  const {
    isAnnouncementBarVisible
  } = useNavLayout();
  const activeIndustry = typeof router.query.industry === "string" ? router.query.industry : "";
  const topSpacing = isAnnouncementBarVisible ? {
    "marginTop": "calc(var(--announcement-bar-height) + var(--header-height))"
  } : {
    "marginTop": "var(--header-height)"
  };
  function onChange(filterType: string, value: string) {
    if (filterType !== "Industry") {
      return;
    }
    const {
      uri: _uri,
      ...queryParams
    } = router.query;
    if (value) queryParams.industry = value;else delete queryParams.industry;
    router.push({
      pathname: clearRouterPath(router.asPath),
      query: queryParams
    }, undefined, {
      shallow: true,
      scroll: false
    });
  }
  function onClearFilters() {
    const {
      uri: _uri,
      industry: _industry,
      ...rest
    } = router.query;
    router.push({
      pathname: clearRouterPath(router.asPath),
      query: rest
    }, undefined, {
      shallow: true,
      scroll: false
    });
  }
  return <_StyledDiv $_css={topSpacing}>
      <_StyledH>{route.title || "Case Studies"}</_StyledH>
      <CaseStudiesFilters industries={caseStudiesData.industries} onChange={onChange} value={{
      industry: activeIndustry
    }} />
      <CaseStudiesGrid limit={CASE_STUDIES_PAGE_SIZE} total={caseStudiesData.total} initialCaseStudies={caseStudiesData.initialCaseStudies} activeIndustry={activeIndustry} middleBlocks={<PageBuilderComponent sections={sections} />} onClearFilters={onClearFilters} />
    </_StyledDiv>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-1v8fteq-0"
})`${p => p.$_css}`;
var _StyledH = /*#__PURE__*/_styled2("h1").withConfig({
  displayName: "case-studies___StyledH",
  componentId: "sc-1jkpgzx-0"
})({
  "position": "absolute",
  "width": "1px",
  "height": "1px",
  "padding": "0",
  "margin": "-1px",
  "overflow": "hidden",
  "clip": "rect(0, 0, 0, 0)",
  "whiteSpace": "nowrap",
  "borderWidth": "0"
});